/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.html.dom.create
import kotlinx.html.*
import kotlinx.html.js.*
import org.w3c.dom.*

/**
 * MFA verification component for the login flow.
 * Shown when user has MFA enabled and needs to enter their TOTP code.
 */
class MfaVerificationComponent(
    private val tempToken: String,
    private val isLoading: Boolean,
    private val errorMessage: String?,
    private val onVerify: (code: String) -> Unit,
    private val onBackToLogin: () -> Unit
) {

    fun render(): HTMLElement {
        return document.create.div {
            style = """
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                background: linear-gradient(180deg, #0a0a0a 0%, #141418 100%);
                padding: 20px;
            """

            div {
                style = """
                    background: rgba(20, 20, 24, 0.9);
                    border-radius: 16px;
                    padding: 40px 32px;
                    box-shadow: 0 8px 32px rgba(0, 217, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
                    width: 340px;
                    max-width: 90vw;
                    backdrop-filter: blur(10px);
                    text-align: center;
                """

                // Lock icon
                div {
                    style = """
                        font-size: 48px;
                        margin-bottom: 16px;
                    """
                    +"🔐"
                }

                // Title
                h2 {
                    style = """
                        color: #ffffff;
                        font-size: 24px;
                        font-weight: 600;
                        margin-bottom: 8px;
                    """
                    +StringKey.AUTH_MFA_TITLE.tr()
                }

                // Subtitle
                p {
                    style = """
                        color: #888;
                        font-size: 14px;
                        margin-bottom: 24px;
                    """
                    +StringKey.AUTH_MFA_DESCRIPTION.tr()
                }

                // Form
                form {
                    id = "mfa-form"
                    onSubmitFunction = { event ->
                        event.preventDefault()
                        val codeInput = document.getElementById("mfa-code") as? HTMLInputElement
                        val code = codeInput?.value ?: ""
                        if (code.length == 6 && !isLoading) {
                            onVerify(code)
                        }
                    }

                    // Code input
                    div {
                        style = "margin-bottom: 16px;"
                        input {
                            type = InputType.text
                            placeholder = "000000"
                            id = "mfa-code"
                            autoFocus = true
                            maxLength = "8" // Allow backup codes too (8 chars)
                            style = """
                                width: 100%;
                                padding: 16px;
                                border: 1px solid #333;
                                border-radius: 8px;
                                font-size: 24px;
                                font-family: monospace;
                                letter-spacing: 8px;
                                text-align: center;
                                box-sizing: border-box;
                                background-color: #1a1a1e;
                                color: #ffffff;
                                outline: none;
                                transition: border-color 0.2s, box-shadow 0.2s;
                            """
                        }
                    }

                    // Error message
                    if (errorMessage != null) {
                        div {
                            style = """
                                color: #ff6b6b;
                                margin-bottom: 16px;
                                font-size: 14px;
                                text-align: center;
                                padding: 10px;
                                background: rgba(255, 107, 107, 0.1);
                                border-radius: 6px;
                            """
                            +errorMessage
                        }
                    }

                    // Verify button
                    button {
                        type = ButtonType.submit
                        style = """
                            width: 100%;
                            padding: 14px;
                            background: linear-gradient(135deg, #00d9ff 0%, #00b8d9 100%);
                            color: #0a0a0a;
                            border: none;
                            border-radius: 8px;
                            font-size: 16px;
                            font-weight: 600;
                            cursor: pointer;
                            transition: transform 0.2s, box-shadow 0.2s;
                            box-shadow: 0 4px 12px rgba(0, 217, 255, 0.3);
                        """
                        id = "verify-btn"
                        if (isLoading) {
                            disabled = true
                            style += "opacity: 0.7; cursor: not-allowed;"
                        }
                        if (isLoading) {
                            +StringKey.AUTH_MFA_VERIFYING.tr()
                        } else {
                            +StringKey.AUTH_MFA_VERIFY.tr()
                        }
                    }
                }

                // Backup code hint
                p {
                    style = """
                        color: #666;
                        font-size: 12px;
                        margin-top: 16px;
                    """
                    +StringKey.MFA_BACKUP_CODE_HINT.tr()
                }

                // Back to login
                button {
                    style = """
                        margin-top: 24px;
                        padding: 10px 20px;
                        background-color: transparent;
                        color: #00d9ff;
                        border: 1px solid #333;
                        border-radius: 6px;
                        font-size: 14px;
                        cursor: pointer;
                        transition: background-color 0.2s;
                    """
                    onClickFunction = { event ->
                        event.preventDefault()
                        onBackToLogin()
                    }
                    +StringKey.MFA_BACK_TO_LOGIN.tr()
                }
            }
        }
    }
}
